import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { z } from 'zod'

export const invitesSearchSchema = z.object({
  page: z.coerce.number().optional(),
  pageSize: z.coerce.number().optional(),
  keyword: z.string().optional(),
})

export type InvitesSearch = z.infer<typeof invitesSearchSchema>

export async function getInvites(queries: InvitesSearch, signal?: AbortSignal) {
  const res = await getApi().invite.$get({ query: stringifyValues(queries) }, { init: { signal } })

  const invites = await res.json()
  return invites
}

export type Invite = Awaited<ReturnType<typeof getInvites>>['invites'][number]

export async function sendInvites(data: { invitations: Invite[] }) {
  const res = await getApi().invite.$post({ json: data })

  const invites = await res.json()
  return invites
}

export async function deleteInvites(invitedEmails: string[]) {
  const res = await getApi().invite.delete.$post({ json: invitedEmails })

  const invites = await res.json()
  return invites
}

export function useInvites(queries: InvitesSearch) {
  return useQuery({
    queryKey: ['invites', queries],
    queryFn: ({ signal }) => getInvites(queries, signal),
  })
}

type SendInvitesResult = Awaited<ReturnType<typeof sendInvites>>

type SendInvitesHandlers = QueryHandlers & {
  onInviteResult?: (data: SendInvitesResult) => void
}

export function useSendInvites({
  handleOnSuccess,
  handleOnError,
  onInviteResult,
}: SendInvitesHandlers) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['send-invites'],
    mutationFn: sendInvites,
    onSuccess: (data) => {
      onInviteResult?.(data)
      handleOnSuccess?.()

      queryClient.invalidateQueries({
        queryKey: ['invites'],
      })
    },
    onError: (error: Error & { details: Record<string, unknown>[] }) => {
      handleOnError?.()

      const details = error.details
        ?.map((detail) => `${detail.reason} (${detail.email})`)
        .join(', ')

      toast.error('Error', {
        description: `${error.message} ${details}`,
      })
    },
  })
}

export function useDeleteInvites() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['delete-invites'],
    mutationFn: deleteInvites,
    onSuccess: () => {
      toast.success('Invitation Removed', {
        description: `The invitation has been successfully removed.`,
      })

      queryClient.invalidateQueries({
        queryKey: ['invites'],
      })
    },
    onError: (error) => {
      toast.error('Error', {
        description: `Invitations could not be removed. ${error.message}`,
      })
    },
  })
}
